import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import api from '../utils/api';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Clock, Package, Users, User, Phone, AlertCircle, Link2, Check, Plus, Lock, Anchor, Star, ChevronDown, ChevronUp, QrCode } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import SignatureCanvas from 'react-signature-canvas';
import MobileWaiverWizard from '../components/MobileWaiverWizard';
import { QRCodeSVG as QRCode } from 'qrcode.react';

// Helper function to format phone number with country code
const formatPhoneNumber = (phone) => {
  if (!phone) return 'N/A';
  
  // Phone is stored in E.164 format (e.g., +1234567890 or +971501234567)
  const cleaned = phone.replace(/\D/g, ''); // Remove all non-digits
  
  // Detect country code
  if (phone.startsWith('+971') || cleaned.startsWith('971')) {
    // UAE number
    const number = cleaned.replace(/^971/, '');
    if (number.length === 9) {
      // Format: +971 50 123 4567
      return `+971 ${number.substring(0, 2)} ${number.substring(2, 5)} ${number.substring(5)}`;
    }
    return `+971 ${number}`;
  } else if (phone.startsWith('+1') || cleaned.length === 10) {
    // US number
    const number = cleaned.replace(/^1/, '').substring(0, 10);
    if (number.length === 10) {
      // Format: +1 (555) 123-4567
      return `+1 (${number.substring(0, 3)}) ${number.substring(3, 6)}-${number.substring(6)}`;
    }
    return `+1 ${number}`;
  }
  
  // Unknown format - return as is
  return phone;
};

// Helper function to format duration smartly
const formatDuration = (minutes) => {
  if (!minutes || minutes <= 0) return 'N/A';
  
  // 60 minutes or less - show in minutes
  if (minutes <= 60) {
    return `${minutes} minutes`;
  }
  
  // Above 60 minutes - calculate hours, days
  const days = Math.floor(minutes / 1440); // 1440 min = 24 hours
  const remainingAfterDays = minutes % 1440;
  const hours = Math.floor(remainingAfterDays / 60);
  const mins = remainingAfterDays % 60;
  
  const parts = [];
  
  if (days > 0) {
    parts.push(days === 1 ? '1 day' : `${days} days`);
  }
  
  if (hours > 0) {
    parts.push(`${hours}hr`);
  }
  
  if (mins > 0) {
    parts.push(`${mins}min`);
  }
  
  return parts.join(' ');
};

const CustomerTicketPage = () => {
  const { ticketId } = useParams();
  const [trip, setBooking] = useState(null);
  const [service, setService] = useState(null); // Add service state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copiedLink, setCopiedLink] = useState(false);
  const [showWaiverModal, setShowWaiverModal] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [signature, setSignature] = useState('');
  const [signingWaiver, setSigningWaiver] = useState(false);
  const [signaturePad, setSignaturePad] = useState(null);
  const [showEmergencyModal, setShowEmergencyModal] = useState(false);
  const [idImage, setIdImage] = useState(null);
  const [idPreview, setIdPreview] = useState(null);
  const [showAllSignatures, setShowAllSignatures] = useState(false); // For collapsing large signature lists
  const [showQRCode, setShowQRCode] = useState(false); // For QR code modal/section
  
  // Branding state
  const [orgBranding, setOrgBranding] = useState({
    logoUrl: '',
    primaryColor: '#007AFF',
    name: ''
  });
  
  // SMS verification state
  const [verificationStep, setVerificationStep] = useState('sms'); // 'sms' or 'id'
  const [phoneNumber, setPhoneNumber] = useState('');
  const [smsCode, setSmsCode] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const [smsVerified, setSmsVerified] = useState(false);
  const [sendingCode, setSendingCode] = useState(false);
  const [verifyingCode, setVerifyingCode] = useState(false);
  
  // Wizard steps
  const [waiverStep, setWaiverStep] = useState(1); // 1=read, 2=name, 3=signature, 4=verify, 5=review
  
  // Magic link auth state
  const [showAuthScreen, setShowAuthScreen] = useState(false);
  const [authEmail, setAuthEmail] = useState('');
  const [requestingAccess, setRequestingAccess] = useState(false);
  const [magicLinkToken, setMagicLinkToken] = useState(null);

  useEffect(() => {
    // Check for magic link token in URL
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    
    if (token) {
      // Has magic link token - try to use it
      setMagicLinkToken(token);
      loadBookingWithToken(token);
    } else {
      // No token - check for existing session
      const sessionToken = localStorage.getItem(`ticket_session_${ticketId}`);
      const sessionExp = localStorage.getItem(`ticket_session_exp_${ticketId}`);
      
      if (sessionToken && sessionExp && Date.now() < parseInt(sessionExp)) {
        // Valid session exists
        setMagicLinkToken(sessionToken);
        loadBookingWithToken(sessionToken);
      } else {
        // No valid session - show auth screen
        setShowAuthScreen(true);
        setLoading(false);
      }
    }
  }, [ticketId]);

  const loadBookingWithToken = async (token) => {
    try {
      const response = await api.get(`/public/bookings/${ticketId}?token=${token}`);
      
      // Response structure: { booking: {...}, service: {...}, organization: {...} }
      const tripData = response.data.booking;
      const serviceData = response.data.service;
      const orgData = response.data.organization;
      
      // Add organization settings to booking
      if (orgData) {
        tripData.waiversEnabled = orgData.waiversEnabled;
        tripData.customWaiverText = orgData.customWaiverText;
        tripData.reviewLinkEnabled = orgData.reviewLinkEnabled;
        tripData.reviewLinkUrl = orgData.reviewLinkUrl;
        tripData.checkInEnabled = orgData.checkInEnabled;
        
        console.log('Check-In Settings:', {
          checkInEnabled: orgData.checkInEnabled,
          checkInStatus: tripData.checkInStatus
        });
      }
      
      setBooking(tripData);
      setService(serviceData); // Store service data
      
      // Set organization branding if available
      if (orgData) {
        setOrgBranding({
          logoUrl: orgData.logoUrl || '',
          primaryColor: orgData.primaryColor || '#007AFF',
          name: orgData.name || ''
        });
      }
      
      // Store session token for 24 hours
      localStorage.setItem(`ticket_session_${ticketId}`, token);
      localStorage.setItem(`ticket_session_exp_${ticketId}`, Date.now() + (24 * 60 * 60 * 1000));
      
      setShowAuthScreen(false);
      setLoading(false);
    } catch (err) {
      console.error('Error loading booking:', err);
      
      if (err.response?.status === 401) {
        // Token expired or invalid
        setShowAuthScreen(true);
        setError('This link has expired or is invalid. Please request a new one below.');
        // Clear invalid session
        localStorage.removeItem(`ticket_session_${ticketId}`);
        localStorage.removeItem(`ticket_session_exp_${ticketId}`);
        // Clean up URL to remove invalid token
        window.history.replaceState({}, '', `/customer-ticket/${ticketId}`);
      } else {
        setError('Booking not found');
      }
      
      setLoading(false);
    }
  };
  
  const handleRequestAccess = async () => {
    if (!authEmail || !authEmail.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }
    
    setRequestingAccess(true);
    try {
      await api.post(`/public/bookings/${ticketId}/request-access`, { email: authEmail });
      toast.success('✅ A new ticket link has been sent to your email!');
      setAuthEmail('');
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error('Email does not match this booking');
      } else if (err.response?.status === 404) {
        toast.error('Booking not found');
      } else {
        toast.error('Failed to send link. Please try again.');
      }
    } finally {
      setRequestingAccess(false);
    }
  };

  const formatTime = (time) => {
    if (!time) return 'N/A';
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const formatDate = (dateStr) => {
    // IMPORTANT: Parse date as local, NOT UTC (prevents timezone shifts)
    // Input: "2025-11-29" should display as Nov 29, not Nov 28
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(year, month - 1, day); // Month is 0-indexed
    
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${days[date.getDay()]}, ${months[date.getMonth()]} ${date.getDate()}`;
  };

  const handleSignWaiver = async () => {
    if (!signature.trim()) {
      toast.error('Please type your full name');
      return;
    }
    
    if (!signaturePad || signaturePad.isEmpty()) {
      toast.error('Please draw your signature');
      return;
    }
    
    // Check verification: SMS OR ID upload
    if (!smsVerified && !idImage) {
      toast.error('Please verify your phone or upload ID');
      return;
    }

    setSigningWaiver(true);
    try {
      // Get device fingerprint
      const deviceInfo = {};
      const safeGet = (fn, fallback = 'Unknown') => {
        try { return fn(); } catch { return fallback; }
      };
      
      deviceInfo.userAgent = safeGet(() => navigator.userAgent);
      deviceInfo.platform = safeGet(() => navigator.platform);
      deviceInfo.language = safeGet(() => navigator.language);
      deviceInfo.languages = safeGet(() => navigator.languages?.join(', '));
      deviceInfo.screenResolution = safeGet(() => `${window.screen.width}x${window.screen.height}`);
      deviceInfo.screenColorDepth = safeGet(() => window.screen.colorDepth);
      deviceInfo.screenPixelDepth = safeGet(() => window.screen.pixelDepth);
      deviceInfo.windowSize = safeGet(() => `${window.innerWidth}x${window.innerHeight}`);
      deviceInfo.timezone = safeGet(() => Intl.DateTimeFormat().resolvedOptions().timeZone);
      deviceInfo.timezoneOffset = safeGet(() => new Date().getTimezoneOffset());
      deviceInfo.cookiesEnabled = safeGet(() => navigator.cookieEnabled);
      deviceInfo.doNotTrack = safeGet(() => navigator.doNotTrack);
      deviceInfo.hardwareConcurrency = safeGet(() => navigator.hardwareConcurrency);
      deviceInfo.maxTouchPoints = safeGet(() => navigator.maxTouchPoints);
      deviceInfo.onLine = safeGet(() => navigator.onLine);
      deviceInfo.vendor = safeGet(() => navigator.vendor);
      deviceInfo.appName = safeGet(() => navigator.appName);
      deviceInfo.appVersion = safeGet(() => navigator.appVersion);
      deviceInfo.oscpu = safeGet(() => navigator.oscpu);
      deviceInfo.productSub = safeGet(() => navigator.productSub);
      deviceInfo.deviceMemory = safeGet(() => navigator.deviceMemory);
      deviceInfo.connection = safeGet(() => navigator.connection ? {
        effectiveType: navigator.connection.effectiveType,
        downlink: navigator.connection.downlink,
        rtt: navigator.connection.rtt,
        saveData: navigator.connection.saveData
      } : null);
      
      // Get compressed signature (15-20KB instead of 100KB)
      const compressedSignature = getCompressedSignature();
      
      // Generate PIN
      const tripIdDigits = (trip.confirmationCode || trip.id).replace(/[^0-9]/g, '');
      const pin = tripIdDigits.slice(-3).padStart(3, '0');
      
      const payload = {
        signature: signature.trim(),
        signatureImage: '', // Skip signature image for now - can cause issues
        smsVerified: smsVerified,
        phoneNumber: phoneNumber,
        idImage: '', // Skip ID for now
        deviceInfo: deviceInfo,
        pin: pin,
        signedAt: new Date().toISOString()
      };
      
      console.log('Signing waiver, SMS verified:', smsVerified);
      console.log('Payload size:', JSON.stringify(payload).length, 'bytes');
      
      await api.post(`/public/trips/${ticketId}/sign-waiver?token=test123`, payload);
      
      toast.success(`Waiver signed! Your PIN is: ${pin}`);
      setShowWaiverModal(false);
      
      // Reset all state
      setSignature('');
      setIdImage(null);
      setIdPreview(null);
      setPhoneNumber('');
      setSmsCode('');
      setCodeSent(false);
      setSmsVerified(false);
      setVerificationStep('sms');
      if (signaturePad) signaturePad.clear();
      
      window.location.reload();
    } catch (error) {
      console.error('Error signing waiver:', error);
      toast.error('Failed to sign waiver. Please try again.');
    } finally {
      setSigningWaiver(false);
    }
  };

  const handleIdUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/') && file.type !== 'application/pdf') {
      toast.error('Please upload an image or PDF');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large. Maximum 5MB');
      return;
    }

    // Convert to base64
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64 = reader.result;
      setIdImage(base64);
      setIdPreview(base64);
      toast.success('ID uploaded!');
    };
    reader.readAsDataURL(file);
  };

  // Format phone number for Twilio (E.164)
  const formatPhoneForTwilio = (input) => {
    const digits = input.replace(/\D/g, ''); // Remove all non-digits
    
    // Already has + prefix, use as-is
    if (input.startsWith('+')) {
      return input.replace(/\D/g, '').length >= 10 ? input : null;
    }
    
    // US number (10 digits)
    if (digits.length === 10) {
      return `+1${digits}`;
    }
    
    // International with country code (11+ digits)
    if (digits.length >= 11) {
      return `+${digits}`;
    }
    
    // UAE shorthand (9 digits) - add +971
    if (digits.length === 9 && input.includes('971')) {
      return `+971${digits.substring(3)}`;
    }
    
    return null; // Invalid
  };

  // Compress signature to small JPEG
  const getCompressedSignature = () => {
    if (!signaturePad || signaturePad.isEmpty()) return null;
    
    const canvas = signaturePad.getTrimmedCanvas();
    // Compress to JPEG at 30% quality (much smaller than PNG)
    return canvas.toDataURL('image/jpeg', 0.3);
  };

  const handleSendSMS = async () => {
    const formatted = formatPhoneForTwilio(phoneNumber);
    
    if (!formatted) {
      toast.error('Please enter a valid phone number');
      return;
    }

    setSendingCode(true);
    try {
      await api.post('/public/sms/send-code', { phoneNumber: formatted });
      
      setPhoneNumber(formatted); // Save formatted version
      setCodeSent(true);
      toast.success('Code sent! Check your phone.');
    } catch (error) {
      console.error('Error sending SMS:', error);
      toast.error('Failed to send code. Try ID upload instead.');
    } finally {
      setSendingCode(false);
    }
  };

  const handleVerifySMS = async () => {
    if (!smsCode || smsCode.length !== 6) {
      toast.error('Please enter the 6-digit code');
      return;
    }

    setVerifyingCode(true);
    try {
      const response = await api.post('/public/sms/verify-code', {
        phoneNumber: phoneNumber, // Already formatted
        code: smsCode
      });
      
      if (response.data.valid) {
        setSmsVerified(true);
        toast.success('Phone verified! ✓');
      } else {
        toast.error('Invalid code. Please try again.');
      }
    } catch (error) {
      console.error('Error verifying code:', error);
      toast.error('Verification failed. Try again or use ID upload.');
    } finally {
      setVerifyingCode(false);
    }
  };

  const getStatusColor = (status) => {
    if (status === 'assigned') return { bg: 'bg-green-500', text: 'Confirmed', color: '#22c55e' };
    if (status === 'partial') return { bg: 'bg-amber-500', text: 'Pending', color: '#f59e0b' };
    return { bg: 'bg-red-400', text: 'Unconfirmed', color: '#ef4444' };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="text-gray-600 mt-4">Loading trip ticket...</p>
        </div>
      </div>
    );
  }

  // Show auth screen if no valid session/token
  if (showAuthScreen) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-gray-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8">
            <div className="text-center mb-6">
              <Lock className="w-16 h-16 text-blue-600 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Verify Your Identity</h2>
              <p className="text-gray-600">
                Enter the email address used for this booking
              </p>
              {error && (
                <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-sm text-yellow-800">{error}</p>
                </div>
              )}
            </div>
            
            <div className="space-y-4">
              <Input
                type="email"
                placeholder="your@email.com"
                value={authEmail}
                onChange={(e) => setAuthEmail(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleRequestAccess()}
                className="text-lg"
                autoFocus
              />
              
              <Button
                onClick={handleRequestAccess}
                disabled={!authEmail.includes('@') || requestingAccess}
                className="w-full text-lg py-6"
                style={{ backgroundColor: '#007AFF' }}
              >
                {requestingAccess ? 'Sending...' : 'Email Me Access Link'}
              </Button>
              
              <p className="text-xs text-gray-500 text-center">
                We'll send you a secure link to view your ticket.<br />
                The link expires 24 hours after sending.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error || !trip) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-6 text-center">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-red-900 mb-2">Booking Not Found</h2>
            <p className="text-red-700">The trip ticket you're looking for doesn't exist or has been deleted.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const statusInfo = getStatusColor(trip.status);
  const waiversEnabled = trip.waiversEnabled || false; // From backend
  const hasAnySignatures = trip.waiverSignatures && trip.waiverSignatures.length > 0;
  const showDetails = !waiversEnabled || hasAnySignatures; // Show if waivers off OR someone signed

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white py-8 px-4">
      <div className="max-w-md mx-auto">
        {/* EXACT COPY of Landing Page Ticket */}
        <div className="bg-white rounded-2xl shadow-2xl border-l-4 p-8" style={{ borderLeftColor: orgBranding.primaryColor }}>
          {/* Header - NO LOGO, just Booking ID + Status */}
          <div className="flex items-center justify-between mb-6 pb-6 border-b border-gray-200">
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wide">Booking ID</p>
              <p className="font-mono font-bold text-lg" style={{ color: orgBranding.primaryColor }}>
                {trip.confirmationCode || trip.id.substring(0, 8).toUpperCase()}
              </p>
            </div>
            <div className="text-right">
              {/* Status Badge - Shows Confirmed OR Checked In with time */}
              {trip.checkInStatus === 'checked_in' ? (
                <span className="text-xs bg-purple-100 text-purple-800 px-3 py-1.5 rounded-full font-semibold flex items-center gap-1.5">
                  <Check className="w-3 h-3" />
                  Checked In {trip.checkInAt && new Date(trip.checkInAt).toLocaleTimeString('en-US', { 
                    hour: 'numeric', 
                    minute: '2-digit' 
                  })}
                </span>
              ) : (
                <span className="text-xs bg-green-100 text-green-800 px-3 py-1 rounded-full font-semibold">Confirmed</span>
              )}
            </div>
          </div>

          {/* Company/Service Name */}
          <h3 className="font-bold text-xl text-gray-900 mb-2 uppercase">
            {service?.name || trip.serviceName || orgBranding.name || 'Loading...'}
          </h3>
          
          {/* Customer Name & Phone - Side by Side */}
          {trip.customerName && (
            <div className="mb-4 pb-4 border-b border-gray-200">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Customer</p>
                  <p className="font-semibold text-gray-900">
                    {(() => {
                      const names = trip.customerName.split(' ');
                      const firstName = names[0];
                      const lastInitial = names[names.length - 1]?.[0] || '';
                      return `${firstName} ${lastInitial}.`;
                    })()}
                  </p>
                </div>
                {trip.customerPhone && (
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Phone</p>
                    <p className="font-semibold text-gray-900">
                      {formatPhoneNumber(trip.customerPhone)}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Booking Details */}
          <div className="space-y-3 mb-6">
            {/* Asset | Team */}
            <div className="grid grid-cols-2 gap-4">
              {trip.assetName && (
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Asset</p>
                  <p className="font-semibold text-gray-900">{trip.assetName}</p>
                </div>
              )}
              
              {trip.teamAssignments && trip.teamAssignments.filter(c => c.teamId).length > 0 && (
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Team</p>
                  <p className="font-semibold text-gray-900">
                    {trip.teamAssignments.filter(c => c.teamId).map(c => {
                      const names = c.teamName.split(' ');
                      const firstName = names[0];
                      const lastInitial = names[names.length - 1]?.[0] || '';
                      return `${firstName} ${lastInitial}.`;
                    }).join(', ')}
                  </p>
                </div>
              )}
            </div>
            
            {/* Date | Time */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Date</p>
                <p className="font-semibold text-gray-900">{formatDate(trip.preferredDate || trip.date)}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Time</p>
                <p className="font-semibold text-gray-900">
                  {formatTime(trip.preferredTime || trip.startTime)} {trip.timezone ? (
                    trip.timezone === 'America/Chicago' ? 'CST' :
                    trip.timezone === 'America/New_York' ? 'EST' :
                    trip.timezone === 'America/Denver' ? 'MST' :
                    trip.timezone === 'America/Los_Angeles' ? 'PST' :
                    trip.timezone === 'America/Phoenix' ? 'MST' :
                    trip.timezone === 'America/Anchorage' ? 'AKST' :
                    trip.timezone === 'Pacific/Honolulu' ? 'HST' : 'CST'
                  ) : ''}
                </p>
              </div>
            </div>
            
            {/* Duration | Meeting Location */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Duration</p>
                <p className="font-semibold text-gray-900">
                  {formatDuration(service?.duration || trip.duration)}
                </p>
              </div>
              {trip.pickupLocation && (
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Meeting Location</p>
                  <p className="font-semibold text-gray-900">{trip.pickupLocation}</p>
                </div>
              )}
            </div>
            
            {/* Guests */}
            <div className="grid grid-cols-2 gap-4">
              {(trip.partySize || (trip.participantNames && trip.participantNames.length > 0)) && (
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Guests</p>
                  <p className="font-semibold text-gray-900">{trip.partySize || trip.participantNames?.length || 0} {(trip.partySize || trip.participantNames?.length) === 1 ? 'person' : 'people'}</p>
                </div>
              )}
            </div>
          </div>

          {/* Who's Signed the Waiver - Green/Orange Box */}
          {waiversEnabled && trip.waiverSignatures && trip.waiverSignatures.length > 0 && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
              <p className="text-xs font-semibold text-green-900 mb-2 uppercase tracking-wide">Who's Signed the Waiver</p>
              <div className="space-y-1">
                {(() => {
                  const MAX_VISIBLE = 5;
                  const totalSignatures = trip.waiverSignatures.length;
                  const visibleSignatures = showAllSignatures 
                    ? trip.waiverSignatures 
                    : trip.waiverSignatures.slice(0, MAX_VISIBLE);
                  const hiddenCount = totalSignatures - MAX_VISIBLE;
                  
                  return (
                    <>
                      {visibleSignatures.map((sig, idx) => {
                        // Camera icon ONLY for team member ID checks (in-person verification)
                        const needsIdCheck = sig.verificationMethod === 'team_member_id_check';
                        
                        return (
                          <div key={idx} className="flex items-center gap-2">
                            {needsIdCheck ? (
                              <>
                                <svg className="w-4 h-4 text-orange-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                                </svg>
                                <span className="text-sm text-orange-700 font-medium">{sig.signatureName}</span>
                              </>
                            ) : (
                              <>
                                <Check className="w-4 h-4 text-green-600 flex-shrink-0" />
                                <span className="text-sm text-green-800">{sig.signatureName}</span>
                              </>
                            )}
                          </div>
                        );
                      })}
                      
                      {/* Show More/Less button for large groups */}
                      {totalSignatures > MAX_VISIBLE && (
                        <button
                          onClick={() => setShowAllSignatures(!showAllSignatures)}
                          className="flex items-center gap-1 text-xs text-green-700 hover:text-green-900 font-medium mt-2 transition-colors"
                        >
                          {showAllSignatures ? (
                            <>
                              <ChevronUp className="w-3 h-3" />
                              Show Less
                            </>
                          ) : (
                            <>
                              <ChevronDown className="w-3 h-3" />
                              See {hiddenCount} More
                            </>
                          )}
                        </button>
                      )}
                    </>
                  );
                })()}
                
                {trip.partySize && trip.waiverSignatures.length < trip.partySize && (
                  <div className="flex items-center gap-2 opacity-50 mt-2 pt-2 border-t border-green-200">
                    <div className="w-4 h-4 border-2 border-gray-400 rounded flex-shrink-0"></div>
                    <span className="text-sm text-gray-600">
                      {trip.partySize - trip.waiverSignatures.length} more {trip.partySize - trip.waiverSignatures.length === 1 ? 'guest' : 'guests'} need to sign
                    </span>
                  </div>
                )}
                {trip.partySize && trip.waiverSignatures.length >= trip.partySize && (
                  <div className="flex items-center gap-2 mt-2 pt-2 border-t border-green-200">
                    <Check className="w-4 h-4 text-green-600" />
                    <span className="text-sm text-green-800 font-medium">
                      All {trip.partySize} {trip.partySize === 1 ? 'guest has' : 'guests have'} signed!
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Actions - Waiver + Review + Copy Link */}
          <div className="space-y-2 pb-6 border-b border-gray-200">
            {waiversEnabled && (() => {
              const signatureCount = trip.waiverSignatures?.length || 0;
              const capacity = trip.partySize || trip.groupSize || 10;
              const isAtCapacity = signatureCount >= capacity;
              
              return (
                <button 
                  onClick={() => !isAtCapacity && setShowWaiverModal(true)}
                  disabled={isAtCapacity}
                  className={`w-full text-white py-3 rounded-lg font-semibold transition-all flex items-center justify-center gap-2 relative ${
                    isAtCapacity ? 'opacity-50 cursor-not-allowed' : 'hover:opacity-90'
                  }`}
                  style={{ background: orgBranding.primaryColor }}
                >
                  <Lock className="w-4 h-4" />
                  <span>Sign Waiver</span>
                  {signatureCount > 0 && (
                    <span className="absolute right-4 text-xs opacity-75">
                      {signatureCount}/{capacity}
                    </span>
                  )}
                </button>
              );
            })()}
            
            {/* Review Button - Prominent */}
            {trip.reviewLinkEnabled && trip.reviewLinkUrl && (
              <a
                href={trip.reviewLinkUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white py-3 rounded-lg font-semibold flex items-center justify-center gap-2 shadow-lg transition-all block"
              >
                <Star className="w-5 h-5 fill-white" />
                Leave a Review
              </a>
            )}
            
            {/* Check-In QR Code Button - Only if enabled AND not yet checked in */}
            {trip.checkInEnabled && trip.checkInStatus !== 'checked_in' && (
              <button
                onClick={() => setShowQRCode(!showQRCode)}
                className="w-full bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white py-3 rounded-lg font-semibold flex items-center justify-center gap-2 shadow-lg transition-all"
              >
                <QrCode className="w-5 h-5" />
                {showQRCode ? 'Hide QR Code' : 'Show QR Code'}
              </button>
            )}
            
            {/* QR Code Display */}
            {trip.checkInEnabled && showQRCode && trip.checkInStatus !== 'checked_in' && (
              <div className="bg-white border-2 border-purple-200 rounded-lg p-6 text-center">
                <div className="inline-block bg-white p-3 rounded-lg border-2 border-gray-300">
                  <QRCode 
                    value={`${window.location.origin}/check-in/${trip.confirmationCode}?org=${trip.organizationId}`}
                    size={200}
                    level="M"
                  />
                </div>
                <p className="text-xs text-gray-600 mt-3">
                  Staff will scan this code to check you in
                </p>
              </div>
            )}
          </div>
          
          {/* Copy Ticket Link Button */}
          <div className="mt-6">
            <button
              onClick={() => {
                // Include magic link token so shared link works
                const baseUrl = window.location.origin;
                const ticketPath = `/customer-ticket/${ticketId}`;
                const link = magicLinkToken 
                  ? `${baseUrl}${ticketPath}?token=${magicLinkToken}`
                  : `${baseUrl}${ticketPath}`;
                  
                navigator.clipboard.writeText(link);
                toast.success('Ticket link copied! Anyone with this link can view the ticket.');
                setCopiedLink(true);
                setTimeout(() => setCopiedLink(false), 2000);
              }}
              className="w-full text-center text-sm text-gray-600 hover:text-blue-600 transition-colors inline-flex items-center justify-center gap-2 py-2"
            >
              <Link2 className="w-4 h-4" />
              {copiedLink ? 'Link Copied!' : 'Copy Ticket Link'}
            </button>
          </div>
        </div>
        
        {/* Powered by Organization (links to BookingFlows) - Outside Card */}
        <div className="text-center mt-6">
          <a 
            href="https://bookingflows.com" 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-xs text-gray-500 hover:text-gray-700 transition-colors inline-flex items-center gap-1"
          >
            Powered by <span className="font-semibold">{orgBranding.name || 'BookingFlows'}</span>
          </a>
        </div>
        
        {/* Waiver Signing Modal */}
        {showWaiverModal && (
          <MobileWaiverWizard
            trip={trip}
            onClose={() => setShowWaiverModal(false)}
            organizationName={orgBranding.name}
            customWaiverText={trip.customWaiverText}
            brandColor={orgBranding.primaryColor}
            onSuccess={() => {
              setShowWaiverModal(false);
              loadBooking();
            }}
          />
        )}
      </div>
    </div>
  );
};

export default CustomerTicketPage;
